import React, { useEffect, useState } from 'react';

const AviationIntro = ({ onComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Start fade out after 3 seconds
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 3000);

    // Complete intro after fade out
    const completeTimer = setTimeout(() => {
      sessionStorage.setItem('aviationIntroSeen', 'true');
      onComplete();
    }, 3800);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-700 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'linear-gradient(135deg, #0B1F3B 0%, #00468D 50%, #0B1F3B 100%)' }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gold Particles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-[#EFBF04] rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              opacity: Math.random() * 0.6 + 0.2,
              boxShadow: '0 0 10px rgba(239, 191, 4, 0.6)'
            }}
          ></div>
        ))}

        {/* Radar Circles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {[200, 400, 600].map((size, i) => (
            <div
              key={i}
              className="absolute top-1/2 left-1/2 border border-[#EFBF04]/20 rounded-full animate-ping"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                marginTop: `-${size/2}px`,
                marginLeft: `-${size/2}px`,
                animationDuration: `${3 + i}s`,
                animationDelay: `${i * 0.5}s`
              }}
            ></div>
          ))}
        </div>

        {/* Light Streaks */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`streak-${i}`}
            className="absolute h-0.5 bg-gradient-to-r from-transparent via-[#EFBF04]/50 to-transparent"
            style={{
              width: '300px',
              top: `${20 + i * 15}%`,
              left: '-300px',
              animation: `slideRight ${3 + i * 0.5}s ease-out infinite`,
              animationDelay: `${i * 0.3}s`
            }}
          ></div>
        ))}
      </div>

      {/* Logo Container */}
      <div className="relative z-10 flex flex-col items-center space-y-8 animate-fade-in-scale">
        {/* Main Logo */}
        <div className="relative">
          <div className="absolute inset-0 bg-[#EFBF04]/20 blur-3xl rounded-full animate-pulse"></div>
          <img 
            src="/turbowing-logo.png" 
            alt="Turbowing Aviation" 
            className="relative w-64 h-auto sm:w-80 md:w-96 object-contain animate-float"
            style={{
              filter: 'drop-shadow(0 0 40px rgba(239, 191, 4, 0.6)) brightness(1.15)'
            }}
          />
        </div>

        {/* Loading Bar */}
        <div className="w-64 h-1 bg-[#00468D]/30 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#EFBF04] to-[#FFD700] rounded-full animate-loading-bar"
            style={{
              boxShadow: '0 0 20px rgba(239, 191, 4, 0.8)'
            }}
          ></div>
        </div>

        {/* Tagline */}
        <p className="text-white/80 text-lg sm:text-xl font-light tracking-widest animate-fade-in-delayed">
          PRECISION • EXCELLENCE • RELIABILITY
        </p>
      </div>

      <style jsx>{`
        @keyframes fade-in-scale {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes loading-bar {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }

        @keyframes slideRight {
          0% {
            left: -300px;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 0;
          }
        }

        @keyframes fade-in-delayed {
          0% {
            opacity: 0;
          }
          60% {
            opacity: 0;
          }
          100% {
            opacity: 0.8;
          }
        }

        .animate-fade-in-scale {
          animation: fade-in-scale 1s ease-out;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-loading-bar {
          animation: loading-bar 3s ease-out forwards;
        }

        .animate-fade-in-delayed {
          animation: fade-in-delayed 3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AviationIntro;
